import React, { useState } from 'react'
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import LlmLanesPanel from '../throughput/LlmLanesPanel'
import LlmUsageReport from '../llmusage/LlmUsageReport'
import JobBatchesView from './JobBatchesView'

/**
 * ActivityTab — the customer-facing face of what the models are doing for this
 * job, in two views of the same subject.
 *
 * **Queue** answers "why is my coding run not finishing?": lane depth read from
 * `llm_request` itself, so it also shows which OTHER kinds of work are
 * competing for the same lane.
 *
 * **Batches** answers "did my import actually turn into work?": every coding
 * batch this job has sent, whichever pipeline sent it, each showing the
 * campaign it is billed to. It is the job-level view of the same table a
 * pipeline's own page shows — a connector tick can create batches across
 * several pipelines, and before this there was no screen that showed them
 * together.
 *
 * **Usage** answers "how much work has this job actually had done?": every
 * model call, coding and Theme AI alike, with tokens and timing.
 *
 * NO MONEY APPEARS HERE. Not because this component hides it, but because the
 * job-scoped endpoint never sends it — see llm_usage_handler.go. That is
 * deliberate: enforcing it in the component would mean a stale build or a
 * copied component could leak margin.
 *
 * Only the selected view mounts, so the unselected one does not fetch; the tab
 * is itself mount-gated by the shell, so leaving the tab stops the poll.
 */
type ActivityView = 'queue' | 'batches' | 'usage'

interface ActivityTabProps {
  customer: string
  job: string
  /** False while the tab is not the visible one — a hidden tab must not poll. */
  active?: boolean
}

const ActivityTab: React.FC<ActivityTabProps> = ({ customer, job, active = true }) => {
  const [view, setView] = useState<ActivityView>('queue')

  return (
    <Box sx={{ p: 3, height: '100%' }} data-testid="autocoder-activity-tab">
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Activity
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        What the models are doing for {job || 'this job'}, and what they have done.
      </Typography>

      <ToggleButtonGroup
        size="small"
        exclusive
        color="info"
        value={view}
        onChange={(_e, next: ActivityView | null) => next && setView(next)}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="queue" data-testid="autocoder-activity-view-queue">
          Queue
        </ToggleButton>
        <ToggleButton value="batches" data-testid="autocoder-activity-view-batches">
          Batches
        </ToggleButton>
        <ToggleButton value="usage" data-testid="autocoder-activity-view-usage">
          Usage
        </ToggleButton>
      </ToggleButtonGroup>

      {/* The lane rows carry neutral `llm-lane-row-<lane>` ids rather than
          autocoder-prefixed ones: LlmLanesPanel is shared with the admin LLM
          Usage page, where an `autocoder-` prefix would simply be false. The
          tab's own id above is the autocoder scope a test narrows to first. */}
      {view === 'queue' && (
        <LlmLanesPanel
          scope={{ kind: 'job', customer, job }}
          active={active}
          title="Model lanes"
          subtitle="This job's share of the LLM request queue — coding and every other client on the lane."
        />
      )}

      {view === 'batches' && <JobBatchesView customer={customer} job={job} active={active} />}

      {view === 'usage' && (
        <LlmUsageReport
          scope={{ kind: 'job', customer, job }}
          // Coding and Theme AI are the two kinds of model work a job does.
          lockedFeatures={['autocoder', 'theme_ai', 'theme_ai_screen']}
          active={active}
          title="Model calls"
          subtitle="Every coding and Theme AI call for this job, newest first."
          dense
        />
      )}
    </Box>
  )
}

export default ActivityTab
